<script lang="ts" setup>
import type {
  AlgoliaProductType,
  AlgoliaAisInfiniteHitsProps,
} from '@integration-layer/components/Algolia/AlgoliaAisInfiniteHits.props'
import type { EditorialContainerData } from '../Editorial/EditorialContainer.props'
import type { ProductCardHorizontal } from '@integration-layer/modules/05.productServices/runtime/components/WrapperProductTile.props'
import { PLP_FIRST_ROW_COMPONENT_ID } from '@integration-layer/data/xGenIds'
import { watchDebounced } from '@vueuse/core'
import { useAlgoliaInsights } from '@integration-layer/composables/useAlgoliaInsights'

const {
  refineAndUpdateState,
  previousCanonicalPage,
  nextCanonicalPage,
  setInfiniteScroll,
  setInfiniteScroll2Page,
  infScroll,
  state,
  loadMoreButton,
} = useAlgoliaAsInfiniteHits()

const props = defineProps<AlgoliaAisInfiniteHitsProps>()
const { breakpoint } = useProjectBreakpoint()
const isMobile = breakpoint.isSmaller('md')

const editorialContent = toRef(props.editorialContent)

const mediaPositionTotalHits = useState<number>('mediaPositionTotalHits')
const isLoading = useLoaderState()

const { rearrangeImagePosition } = await useTemplatePlp()

const loadLessButton = ref<HTMLDivElement | null>(null)

const { getInstance } = useInstantSearch()
const instance = getInstance()
const { trackClickedItem, setSearchQueryID } = useAlgoliaInsights()

export type WrappedItem = {
  type: 'product' | 'editorial'
  key: string
  content: AlgoliaProductType | EditorialContainerData
}

const storageStage = useState('_instantsearchStorageState')

const itemsWithEditorialContent = computed(() => {
  const editorialContentMap = new Map<number, EditorialContainerData>()
  let counter = 0
  let counterItem = -1
  const wrappedItems: WrappedItem[] = []

  state.value.hits.forEach((item, index) => {
    const algoliaItem = item as unknown as AlgoliaProductType
    wrappedItems.push({
      key: `productTile-${index}-${item.objectID}`,
      type: 'product',
      content: {
        ...algoliaItem,
        // add separately the price fetched from CommerceLayer
        clPrices: props?.productPrices?.[algoliaItem?.size?.[0]?.SKU ?? ''],
      },
    })
  })

  if (!editorialContent.value) return wrappedItems

  const multiplePerRow = isMobile ? 2 : 4

  editorialContent.value
    ?.sort((a, b) => Number(a.position) - Number(b.position))
    .forEach((content: any) => {
      counter++
      if (content.position === '1') {
        const index = Number(content.position) - 1
        editorialContentMap.set(index, content)
      } else {
        const index = Number(content.position) * multiplePerRow + counter
        editorialContentMap.set(index, content)
      }
    })

  editorialContentMap.forEach((editorialContent: any) => {
    counterItem++

    const position =
      Number(editorialContent?.position) * multiplePerRow + counterItem
    wrappedItems.splice(position, 0, {
      key: `editorial-container-wrapper-${editorialContent.listOfLogicWrapper?.[0]?.name}`,
      type: 'editorial',
      content: editorialContent,
    })
  })

  return wrappedItems
})
const initViewItemList = () => {
  nextTick(() => {
    useGAViewItemListEvent(itemsWithEditorialContent.value, 0, true)
  })
}

onMounted(() => {
  initViewItemList()
})

const currentFilters = computed(() => {
  const algoliaFilters = storageStage.value

  return Object.values(
    //@ts-ignore
    algoliaFilters?.index?.state?.disjunctiveFacetsRefinements || {}
    //@ts-ignore
  ).reduce((sum, array) => sum + array.length, 0)
})

watchDebounced(
  currentFilters,
  () => {
    useGAViewItemListEvent(itemsWithEditorialContent.value, 0, false)
  },
  { debounce: 500, maxWait: 1000 }
)

// Watcher that is used by Armani Loader when a new hits is loaded the loader disappear
watch(
  () => state.value.hits,
  () => {
    isLoading.value = false
  }
)

const handleProductClick = (
  productHit: AlgoliaProductType & {
    __position?: number
    __queryID?: string
  }
) => {
  setSearchQueryID(productHit?.__queryID ?? '')
  trackClickedItem(
    instance.value.indexName,
    'Product Clicked',
    [productHit.objectID],
    productHit.__queryID || '',
    [productHit.__position || 0]
  )
}

const clickLoadMore = (currentHits: number) => {
  // Give time to the computed to update and then send event
  setTimeout(() => {
    useGAViewItemListEvent(itemsWithEditorialContent.value, currentHits, false)
  }, 1000)
}

// Open quick buy
const { openQuickBuySlide } = await useUIQuickBuyPopUpSlide()

const openQuickBuyPopUpLayer = (
  product: ProductCardHorizontal,
  originalItem?: AlgoliaProductType & {
    indexName?: string
    objectID?: string
    __position?: number
    __queryID?: string
  }
) => {
  const enhancedProduct = {
    ...product,
    indexName: instance.value.indexName,
    objectID: originalItem?.objectID,
    __position: originalItem?.__position,
    __queryID: originalItem?.__queryID,
  }

  openQuickBuySlide(
    {
      product: enhancedProduct,
      isQuickBuyPlp: true,
    },
    {
      saveChanges: () => {},
    }
  )
}

const sliceProducts = (products: AlgoliaProductType[]) => {
  switch (props.gridConfiguration.name) {
    case 'grid321':
      // grid-cols-2 md:grid-cols-2 lg:grid-cols-3
      return products.slice(0, 3)
    case 'grid432':
      // grid-cols-1 md:grid-cols-3 lg:grid-cols-4
      return products.slice(0, 4)
    default:
      // grid-cols-3 md:grid-cols-5 lg:grid-cols-6
      return products
  }
}
</script>

<template>
  <RedirectPLPIfNoItems v-if="state.hits?.length === 0" />
  <AisInfiniteHits>
    <template #loadPrevious="{ isFirstPage, refinePrevious }">
      <div>
        <div
          v-if="!isFirstPage"
          ref="loadLessButton"
          class="flex items-center justify-center py-8 md:py-12 lg:py-16"
        >
          <a
            class="cta cta-l cta-primary"
            :href="previousCanonicalPage"
            @click.prevent="refineAndUpdateState(refinePrevious, true)"
          >
            {{ $ts('plp.showLess') }}
          </a>
        </div>
      </div>
    </template>
    <template #default="{ refineNext, isLastPage }">
      <div
        v-if="
          xGenFirstRowComponent &&
          !hasFilters &&
          gridConfiguration.name !== 'gridMoodboard' &&
          !loadLessButton
        "
        :id="`XSE-${PLP_FIRST_ROW_COMPONENT_ID}`"
      >
        <XGenRecommendations :prediction="PLP_FIRST_ROW_COMPONENT_ID">
          <template #default="{ products, status }">
            <PlpGrid
              v-show="status === 'idle' || status === 'pending'"
              :grid-configuration="gridConfiguration"
              only-border-top
              show-only-first-row
            >
              <li v-for="index in 6" :key="index">
                <SkeletonUIProductTile
                  :only-img="gridConfiguration.name === 'gridWall'"
                />
              </li>
            </PlpGrid>

            <PlpGrid
              v-show="status === 'success'"
              :grid-configuration="gridConfiguration"
              only-border-top
              show-only-first-row
            >
              <li
                v-for="(item, index) in sliceProducts(products)"
                :key="item?.objectID ?? index"
                class="wrapper-class col-span-1"
              >
                <WrapperProductTile :product="item">
                  <template #default="{ product, productCardHorizontal }">
                    <UIProductTile
                      :id="`productTile-${product.sku}`"
                      xse-type="rec"
                      :xse-prod-code="product.sku"
                      fetch-priority-image
                      :only-img="gridConfiguration.name === 'gridWall'"
                      :product="{
                        ...product,
                        linkToProduct: `${product.linkToProduct}?xse_prod_code=${product.sku}&xse=${PLP_FIRST_ROW_COMPONENT_ID}`,
                        images: rearrangeImagePosition(
                          product.images,
                          index,
                          mediaPositionTotalHits
                        ),
                      }"
                      :force-wishlist-refresh="false"
                      @open-quick-buy="
                        () =>
                          productCardHorizontal &&
                          openQuickBuyPopUpLayer(productCardHorizontal)
                      "
                    />
                  </template>
                </WrapperProductTile>
              </li>
            </PlpGrid>
          </template>
        </XGenRecommendations>
      </div>
      <PlpGrid :grid-configuration="gridConfiguration">
        <template
          v-for="(item, index) in itemsWithEditorialContent"
          :key="`${index}_${item.key}`"
        >
          <li
            v-if="
              !props.hasFilters &&
              gridConfiguration.name === 'gridMoodboard' &&
              item.type === 'editorial'
            "
            :key="`editorial-container-wrapper-${(item as unknown as any).listOfLogicWrapper?.[0]?.name}`"
            class="wrapper-class col-span-12"
          >
            <EditorialContainer
              :data="item.content as EditorialContainerData"
            />
          </li>
          <li
            v-if="item.type === 'product'"
            :class="
              gridConfiguration.name === 'gridMoodboard'
                ? 'wrapper-class col-span-6 md:col-span-3'
                : 'wrapper-class col-span-1'
            "
          >
            <WrapperProductTile
              :id="item.key"
              :data-testid="`productTitle-${index}`"
              :product="item.content as unknown as AlgoliaProductType"
            >
              <template #default="{ product, productCardHorizontal }">
                <UIProductTile
                  :id="`productTile-${product.sku}`"
                  :fetch-priority-image="index < 12"
                  :product="{
                    wishlistSizeValue: getUniqueSizeProductLabel(
                      item?.content as AlgoliaProductType
                    ),
                    ...product,
                    images: rearrangeImagePosition(
                      product.images,
                      index,
                      mediaPositionTotalHits
                    ),
                  }"
                  :only-img="gridConfiguration.name === 'gridWall'"
                  :force-wishlist-refresh="false"
                  @click="
                    handleProductClick(
                      item.content as unknown as AlgoliaProductType
                    )
                  "
                  @open-quick-buy="
                    () =>
                      productCardHorizontal &&
                      openQuickBuyPopUpLayer(
                        productCardHorizontal,
                        item.content as unknown as AlgoliaProductType
                      )
                  "
                />
              </template>
            </WrapperProductTile>
          </li>
        </template>
      </PlpGrid>
      <AisStats v-if="!isLastPage">
        <template #default="{ nbHits }">
          <div class="mt-lg" @click="clickLoadMore(state.hits?.length)">
            <div id="loadMoreStrip" class="h-2 w-full"></div>

            <div class="flex flex-col items-center justify-center gap-2 py-6">
              <a
                ref="loadMoreButton"
                :class="[
                  {
                    hidden: infScroll,
                  },
                ]"
                :href="nextCanonicalPage"
                class="cta cta-l cta-primary text-button-2 w-[247px] min-w-max md:w-[218px]"
                @click.prevent="refineAndUpdateState(refineNext)"
              >
                {{ `${$ts('plp.showMore')} (${state.hits?.length}/${nbHits})` }}
              </a>

              <AtomsCta
                v-if="!infScroll"
                :icon-show-default="false"
                anatomy="ghost"
                @click-handler="
                  nbHits / state.hits?.length > 2
                    ? setInfiniteScroll()
                    : setInfiniteScroll2Page()
                "
              >
                <template #label>
                  {{ $ts('plp.showAll') }}
                </template>
              </AtomsCta>
              <div id="loadMoreButton" class="h-[100%] w-[100vw]"></div>
            </div>
          </div>
        </template>
      </AisStats>
    </template>
  </AisInfiniteHits>
</template>
